<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            rowClass="text-center"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <div class="ppv2-flex-1"></div>

                    <!-- Create Button -->
                    <a
                        href="https://balsam-payment-a6b.notion.site/T-l-charger-PlayProPOS-3ddef4c47c1f80169415dca4a8278be0?pvs=73"
                        target="_blank"
                        class="ppv2-min-h-[40px] ppv2-text-black ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-black ppv2-border-opacity-20 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-transition-all ppv2-shadow-lg"
                    >
                        <span
                            class="ppv2-flex ppv2-items-center ppv2-gap-3 poppinsMedium ppv2-text-black"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <Download :size="20" color="currentColor" />
                            {{ $t('pos.downloadPOS') }}
                        </span>
                    </a>
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="handleCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular"> {{ $t('pos.add') }} </span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Device ID -->
            <template #column-device_id="{ item }">
                <a
                    href="#"
                    @click.prevent="handleEdit(item)"
                    class="ppv2-text-black poppinsRegular ppv2-transition-colors hover:ppv2-text-[#7D2DBD] ppv2-underline"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.device_id }}
                </a>
            </template>

            <!-- Column: Device Name -->
            <template #column-device_name="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.device_name || '-' }}
                </span>
            </template>

            <!-- Column: Cash Fund -->
            <!-- <template #column-cash_fund="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.cash_fund }}
                </span>
            </template> -->

            <!-- Column: Sent By
            <template #column-sent_by="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.request_sent_by ? item.request_sent_by.first_name + ' ' + item.request_sent_by.last_name : '-' }}
                </span>
            </template>-->

            <!-- Column: Status -->
            <template #column-is_active="{ item }">
                <span
                    class="poppinsRegular ppv2-px-3 ppv2-py-1 ppv2-rounded-full ppv2-text-xs"
                    :class="
                        item.is_active
                            ? 'ppv2-bg-green-100 ppv2-text-green-700'
                            : item.request_sent_by && !item.authorized_by
                              ? 'ppv2-bg-gray-100 ppv2-text-gray-700'
                              : 'ppv2-bg-red-100 ppv2-text-red-700'
                    "
                >
                    {{
                        item.is_active
                            ? $t('pos.status_active')
                            : item.request_sent_by && !item.authorized_by
                              ? $t('pos.status_pending')
                              : $t('pos.status_inactive')
                    }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[200px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <div
                            @click="handleEdit(item)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('pos.action_edit')
                            }}</span>
                        </div>

                        <!-- Activer / Désactiver -->
                        <div
                            @click="toggleStatus(item)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <component :is="item.is_active ? 'XCircle' : 'CheckCircle'" :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ item.is_active ? $t('pos.action_deactivate') : $t('pos.action_activate') }}
                            </span>
                        </div>

                        <!-- Supprimer -->
                        <div
                            @click="deleteSingleItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('pos.action_delete')
                            }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ $t('pos.selected_items', { count: selectedCount }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- POS Form Modal -->
        <POSForm v-model="showFormPopup" :posData="editingItem" :userId="userId" @success="handleFormSuccess" :token="token" />

        <!-- Delete Confirmation Popup -->
        <CustomDialog
            v-model="showDeletePopup"
            :defaultTitle="$t('pos.delete_title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { FilePen, Trash2, Plus, MoreVertical, CheckCircle, XCircle, Download } from 'lucide-vue'
import DataTable from '../../Shared/DataTable.vue'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'
import POSForm from './POSForm.vue'
import axios from 'axios'
import { eventBus } from '../../Customer/eventBus'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'POSTable',
    components: {
        FilePen,
        Trash2,
        Plus,
        MoreVertical,
        CheckCircle,
        XCircle,
        Download,
        DataTable,
        Search,
        BaseButton,
        CustomDialog,
        POSForm,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('pos_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            selectedItems: [],
            selectAll: false,
            showDeletePopup: false,
            showFormPopup: false,
            editingItem: null,
            itemToDelete: null,
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            sortColumn: 'id',
            sortDirection: 'desc',
            selectedColumnKeys: ['device_id', 'device_name', 'sent_by', 'is_active', 'actions'],
            deletePopupDescription: '',
        }
    },
    computed: {
        allColumns() {
            return [
                { key: 'device_id', label: this.$t('pos.device_id_label'), sortable: true },
                { key: 'device_name', label: this.$t('pos.device_name_label'), sortable: true },
                // { key: 'cash_fund', label: 'Fond de caisse', sortable: true },
                { key: 'sent_by', label: this.$t('pos.column_requested_by'), sortable: false },
                { key: 'is_active', label: this.$t('pos.status_label'), sortable: true },
                { key: 'actions', label: this.$t('common.actions'), sortable: false },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    mounted() {
        this.fetchData()
        window.addEventListener('scroll', this.updateDropdownPosition)
        window.addEventListener('resize', this.updateDropdownPosition)
        document.addEventListener('click', this.handleClickOutside)
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.updateDropdownPosition)
        window.removeEventListener('resize', this.updateDropdownPosition)
        document.removeEventListener('click', this.handleClickOutside)
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            this.fetchData()
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            this.fetchData()
        },
        handleClickOutside(event) {
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]
                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu
                if (!btnEl?.contains(event.target) && !menuEl?.contains(event.target)) {
                    this.activeActionMenu = null
                }
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return
            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 200}px`,
            }
        },
        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        async fetchData() {
            this.loading = true
            try {
                const response = await axios.get('/api/v3/pos/list', {
                    params: {
                        currentPage: this.currentPage,
                        itemsPerPage: this.itemsPerPage,
                        search: this.search,
                        orderBy: this.sortColumn,
                        orderDirection: this.sortDirection,
                    },
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.tableData = response.data.data
                    this.recordsTotal = response.data.total
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.tableData.length
        },

        toggleSelectAll() {
            const allIds = this.tableData.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },
        handleCreate() {
            this.editingItem = null
            this.showFormPopup = true
        },
        handleEdit(item) {
            this.activeActionMenu = null
            this.editingItem = item
            this.showFormPopup = true
        },
        handleFormSuccess() {
            this.fetchData()
        },
        deleteSingleItem(itemId) {
            this.deletePopupDescription = this.$t('pos.delete_confirm_single')
            this.activeActionMenu = null
            this.itemToDelete = itemId
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                await axios.delete(`/api/v3/pos/delete/${this.itemToDelete}`, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                this.$toast.success(this.$t('pos.delete_success'))
                this.fetchData()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting POS:', error)
                this.$toast.error(this.$t('common.delete_error'))
            } finally {
                this.showDeletePopup = false
                this.itemToDelete = null
            }
        },
        async toggleStatus(item) {
            this.activeActionMenu = null
            try {
                const response = await axios.patch(
                    `/api/v3/pos/toggle-status/${item.id}`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )
                if (response.data.success) {
                    this.$toast.success(item.is_active ? this.$t('pos.deactivated') : this.$t('pos.activated'))
                    this.fetchData()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error toggling POS status:', error)
                this.$toast.error(this.$t('common.status_change_error'))
            }
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('products.deletePopup.message')
            this.showDeletePopup = true
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('pos_items_per_page', this.itemsPerPage)
            this.fetchData()
        },
    },
    watch: {
        showFormPopup(val) {
            if (!val) {
                this.editingItem = null
            }
        },
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.tableData.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.actionsDropdown {
    background: white;
    border: 1px solid #e2e8f0;

    .actionItem {
        color: #4a5568;
        &:hover {
            background: #f7fafc;
            color: #7d2dbd;
        }
    }
}
</style>
